import React from "react";
import DicotomicCheckbox from "../../../../../components/dicotomic-checkbox/dicotomic-checkbox";
import InputBox from "../../../../../components/input-box/input-box";
import SelectBox from "../../../../../components/select-box/select-box";
import { deepGet } from "../../../../../utils/utility";
import { MascotasTableContainer } from "./mascotas-table-style";
import { CoverageItem, Field, MascotasTableProps } from "./mascotas-table-types";

const MascotasTable = ({
  coverages,
  formData,
  index,
  otherPolicy,
  companies,
  rawCoverages,
  changeValue,
  changeValueCoverage,
  getOtherInsurance,
}: MascotasTableProps) => {
  // coveragesProps son las coberturas del producto
  // coverages son el listado de coberturas por mascota
  const othersPolicy = ["RC01", "AV04"];
  const { coverages: coveragesProps, isReadOnly } = formData;

  const mainCoverages: CoverageItem[] = deepGet(coverages, "main.items") || [];
  const complementaryCoverages: CoverageItem[] = deepGet(coverages, "complementary.items") || [];

  const renderSelectOptions = (code: string) => {
    const capital: Field<string> | undefined = deepGet(coveragesProps, `${code}.capital`);
    const options = deepGet(coveragesProps, `${code}.options`);

    return (
      <div className="h-auto">
        <div className="selectBox flex justifyFlex-end flex-no-wrap">
          <label className="point">&nbsp;</label>
          <SelectBox
            placeholder="Seleccionar capital"
            required={true}
            errorCode={capital && capital.errorCode}
            optionsText={options as { label: string; value: string }[]}
            initialValue={capital && capital.value}
            defaultValue={capital && capital.value}
            optionKey={"value"}
            optionValue={"label"}
            onChange={(value: string) => {
              changeValueCoverage(value, "coverages", code, "capital");
            }}
          />
        </div>
      </div>
    );
  };

  const renderOtherInsuranceOptions = (code: string, name: string) => {
    const otherInsuranceCompany: Field<string> | undefined = deepGet(
      coveragesProps,
      `${code}.otherInsurance.company`
    );
    const otherInsuranceNumberPolicy: Field<string> | undefined = deepGet(
      coveragesProps,
      `${code}.otherInsurance.numberPolicy`
    );

    return (
      <>
        <div className="flex colSin-100">
          <div className="pl-38">
            <p className="content-wrapper">¿Posee ya un seguro de {name}?</p>
          </div>
          <div className="flex">
            <div className="radioBox pt-5">
              <label>
                <span>Sí</span>
                <input
                  type="radio"
                  name={`otherPolicy${code}-${index && Number(index) + 1}`}
                  value={"Si"}
                  checked={otherPolicy[code] === "Si" && companies.length !== 0}
                  onChange={(ev) => getOtherInsurance(code, ev)}
                />
              </label>
            </div>
            <div className="radioBox pt-5">
              <label>
                <span>No</span>
                <input
                  type="radio"
                  name={`otherPolicy${code}-${index && Number(index) + 1}`}
                  value={"No"}
                  checked={otherPolicy[code] === "No" || companies.length === 0}
                  onChange={(ev) => getOtherInsurance(code, ev)}
                />
              </label>
            </div>
          </div>
        </div>
        <div className="flex colSin-100">
          <div className="flex">
            <div className="flex align-center pl-38">
              <label className="content-wrapper">¿En qué compañía?</label>
            </div>
            <div className="selectBox flex justify-start">
              {otherInsuranceCompany && otherInsuranceCompany.required && (
                <label className="point">&nbsp;</label>
              )}
              <SelectBox
                placeholder="Selecciona"
                required={otherInsuranceCompany ? otherInsuranceCompany.required : false}
                errorCode={otherInsuranceCompany ? otherInsuranceCompany.errorCode : ""}
                optionsText={companies as { label: string; value: string }[]}
                initialValue={
                  companies.length !== 0 && otherInsuranceCompany ? otherInsuranceCompany.value : ""
                }
                defaultValue={
                  companies.length !== 0 && otherInsuranceCompany ? otherInsuranceCompany.value : ""
                }
                optionKey={"value"}
                optionValue={"label"}
                onChange={(value: string) => {
                  changeValueCoverage(value, "coverages", code, "otherInsurance", "company");
                }}
                disabled={
                  (otherPolicy && otherPolicy[code] === "No") ||
                  coveragesProps[code].otherInsurance === null ||
                  companies.length === 0
                }
              />
            </div>
          </div>
          <div className="flex">
            <div className="flex align-center pl-38">
              <label className="content-wrapper">Nº Póliza</label>
            </div>
            <div className="inputBox colSin-30 flex justify-start flex-no-wrap">
              {deepGet(coveragesProps, `${code}.otherInsurance.numberPolicy.required`) && (
                <label className="point">&nbsp;</label>
              )}
              <InputBox
                type="number"
                required={otherInsuranceNumberPolicy ? otherInsuranceNumberPolicy.required : false}
                errorCode={otherInsuranceNumberPolicy ? otherInsuranceNumberPolicy.errorCode : ""}
                value={
                  companies.length !== 0 && otherInsuranceNumberPolicy
                    ? otherInsuranceNumberPolicy.value
                    : ""
                }
                onChange={(value: string) =>
                  changeValueCoverage(value, "coverages", code, "otherInsurance", "numberPolicy")
                }
                disabled={
                  isReadOnly ||
                  (otherPolicy && otherPolicy[code] === "No") ||
                  coveragesProps[code].otherInsurance === null ||
                  companies.length === 0
                }
                notScroll
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const getClassNames = (): { main: string; complementary: string } => {
    const hasMainCovs = rawCoverages.some((cov) => cov.required);
    const hasComplementaryCovs = rawCoverages.some((cov) => !cov.required);
    const hasMainCapitals = rawCoverages.some((cov) =>
      cov.required && cov.capitales ? true : false
    );
    const hasComplementaryCapitals = rawCoverages.some((cov) =>
      !cov.required && cov.capitales ? true : false
    );

    if (hasMainCapitals && hasComplementaryCapitals) {
      return {
        main: "halfWidth",
        complementary: "halfWidth",
      };
    }

    if (hasMainCovs && !hasComplementaryCovs) {
      return {
        main: "fullWidth",
        complementary: "noWidth",
      };
    }

    return {
      main: "oneThirdWidth",
      complementary: "twoThirdWidth",
    };
  };

  const classNames = getClassNames();

  return (
    <MascotasTableContainer className="personal-data-container mb-10" key={Number(index)}>
      <div className="col-100 mb-1 borderB-white">
        <div className="col-25 table-bgBlue content-wrapper">Mascota {Number(index) + 1}</div>
      </div>
      <div className={`${classNames.main} table-tr mr-2per`}>
        <p className="table-bgBlue content-wrapper">GARANTÍAS OBLIGATORIAS</p>
        {mainCoverages.map((coverage) => {
          const { code, name } = coverage;
          const rawCoverage = rawCoverages.find((cov) => cov.code === code);
          const hasCapitals = rawCoverage && rawCoverage.capitales;

          return (
            <div key={coverage.code}>
              <div className={`${hasCapitals ? "flex-content-wrapper" : "content-wrapper"}`}>
                <p>{coverage.name}</p>
                {hasCapitals && renderSelectOptions(code)}
              </div>

              {coveragesProps[code].value &&
                othersPolicy.includes(code) &&
                renderOtherInsuranceOptions(code, name)}
            </div>
          );
        })}
      </div>
      {complementaryCoverages.length > 0 && (
        <div className={`${classNames.complementary} table-tr`}>
          <p className="table-bgBlue content-wrapper">GARANTÍAS OPCIONALES</p>
          {complementaryCoverages.map((coverage) => {
            const { code, disabled, value, name } = coverage;
            return (
              <div
                className={
                  coveragesProps[code].value
                    ? "flex-space-between plr-20"
                    : " flex-space-between content-wrapper"
                }
                key={code}
              >
                <DicotomicCheckbox
                  initChecked={coveragesProps[code].value}
                  disabled={disabled || formData.isReadOnly}
                  className={value === true ? "optional pt-10" : "optional"}
                  checkboxText={coveragesProps[code].name}
                  onChange={(value: string) => {
                    changeValue(value, "coverages", code);
                  }}
                />
                {coveragesProps[code].value && renderSelectOptions(code)}
                {coveragesProps[code].value &&
                  othersPolicy.includes(code) &&
                  renderOtherInsuranceOptions(code, name)}
              </div>
            );
          })}
        </div>
      )}
    </MascotasTableContainer>
  );
};

export default MascotasTable;
